import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BhajanCard, BhajanCardData } from './BhajanCard';
import { Button, EmptyState, cn } from '../ui';
import { PAGE_SIZE, nextVisibleCount } from './gridWindowing';

export type Density = 'compact' | 'comfortable' | 'cozy';
export type GridLayout = 'grid' | 'list';

export interface BhajanGridProps {
  bhajans: BhajanCardData[];
  language: string;
  density?: Density;
  layout?: GridLayout;
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  onSelect?: (data: BhajanCardData) => void;
  className?: string;
}

const gridClass: Record<Density, string> = {
  compact:     'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  comfortable: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  cozy:        'grid-cols-1 lg:grid-cols-2',
};

export const BhajanGrid: React.FC<BhajanGridProps> = ({
  bhajans,
  language,
  density = 'comfortable',
  layout = 'grid',
  isLoading,
  emptyTitle = 'No bhajans found',
  emptyDescription = 'Try adjusting your filters or searching for something else.',
  emptyAction,
  onSelect,
  className,
}) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset to the first page whenever the result set changes (new search/filter).
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [bhajans]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisibleCount((c) => nextVisibleCount(c, bhajans.length, PAGE_SIZE));
      }
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [bhajans.length]);

  const visible = bhajans.slice(0, visibleCount);
  const hasMore = visibleCount < bhajans.length;

  const loadMore = (
    <div ref={sentinelRef} className="mt-6 flex justify-center">
      <Button
        variant="secondary"
        onClick={() => setVisibleCount((c) => nextVisibleCount(c, bhajans.length, PAGE_SIZE))}
      >
        Load more
      </Button>
    </div>
  );

  if (isLoading) {
    if (layout === 'list') {
      return (
        <div className={cn('rounded-2xl border border-light bg-bg-2 divide-y divide-white/5', className)}>
          {Array.from({ length: 8 }).map((_, i) => (
            <BhajanCard
              key={i}
              data={{ nodeId: -1 - i, title: '' }}
              language={language}
              variant="list-row"
              isLoading
            />
          ))}
        </div>
      );
    }
    return (
      <div className={cn('grid gap-4 sm:gap-5', gridClass[density], className)}>
        {Array.from({ length: 8 }).map((_, i) => (
          <BhajanCard
            key={i}
            data={{ nodeId: -1 - i, title: '' }}
            language={language}
            isLoading
          />
        ))}
      </div>
    );
  }

  if (bhajans.length === 0) {
    return (
      <EmptyState
        icon={<CosmicEmptyIcon />}
        title={emptyTitle}
        description={emptyDescription}
        action={
          emptyAction ?? (
            <Link to="/bhajans">
              <Button variant="primary">Reset</Button>
            </Link>
          )
        }
      />
    );
  }

  if (layout === 'list') {
    return (
      <>
        <div className={cn('rounded-2xl border border-light bg-bg-2 divide-y divide-white/5 overflow-hidden', className)}>
          {visible.map((b) => (
            <BhajanCard
              key={b.nodeId}
              data={b}
              language={language}
              variant="list-row"
              onClick={onSelect ? () => onSelect(b) : undefined}
            />
          ))}
        </div>
        {hasMore && loadMore}
      </>
    );
  }

  return (
    <>
      <div className={cn('grid gap-4 sm:gap-5 auto-rows-fr', gridClass[density], className)}>
        {visible.map((b) => (
          <BhajanCard
            key={b.nodeId}
            data={b}
            language={language}
            onClick={onSelect ? () => onSelect(b) : undefined}
          />
        ))}
      </div>
      {hasMore && loadMore}
    </>
  );
};

const CosmicEmptyIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden>
    <circle cx="32" cy="32" r="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 4" />
    <circle cx="14" cy="14" r="1.4" fill="currentColor" opacity="0.6" />
    <circle cx="50" cy="20" r="1.0" fill="currentColor" opacity="0.4" />
    <circle cx="48" cy="48" r="1.2" fill="currentColor" opacity="0.5" />
    <circle cx="16" cy="46" r="0.8" fill="currentColor" opacity="0.4" />
    <path d="M32 22 L34 30 L42 32 L34 34 L32 42 L30 34 L22 32 L30 30 Z" fill="currentColor" opacity="0.7" />
  </svg>
);
